import { create } from 'zustand';

export type ThemePref = 'light' | 'dark' | 'system';

function read(): ThemePref {
  try {
    const v = localStorage.getItem('vatman.theme');
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // Storage may be unavailable (private mode); fall back to the system theme.
  }
  return 'system';
}

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function resolve(pref: ThemePref): 'light' | 'dark' {
  if (pref === 'system') return media?.matches ? 'dark' : 'light';
  return pref;
}

interface ThemeState {
  pref: ThemePref;
  resolved: 'light' | 'dark';
  setPref: (p: ThemePref) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  pref: read(),
  resolved: resolve(read()),
  setPref: (pref) => {
    try {
      localStorage.setItem('vatman.theme', pref);
    } catch {
      // Ignore: the choice still applies for this session.
    }
    const resolved = resolve(pref);
    document.documentElement.dataset.theme = resolved;
    set({ pref, resolved });
  },
}));

media?.addEventListener('change', () => {
  const { pref } = useTheme.getState();
  if (pref !== 'system') return;
  const resolved = resolve(pref);
  document.documentElement.dataset.theme = resolved;
  useTheme.setState({ resolved });
});
